<template>
  <div
    v-if="showNotice"
    class="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg"
  >
    <div class="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <!-- Cookie Notice Text -->
        <div class="flex-1">
          <p class="text-sm text-gray-700">
            We use cookies to enhance your experience, analyze site traffic, and personalize content. 
            By continuing to use our site, you consent to our use of cookies. 
            <a href="/privacy" humblytics="cookie-privacy-link" class="text-blue-600 hover:text-blue-800 underline">Learn more</a>
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-3 sm:gap-2">
          <!-- Decline Button -->
          <button
            @click="declineCookies"
            humblytics="cookie-decline"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Decline
          </button>

          <!-- Accept Button -->
          <button
            @click="acceptCookies"
            humblytics="cookie-accept"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// GDPR-applicable regions (EU/EEA + UK)
// These timezones indicate users likely subject to GDPR/ePrivacy
const GDPR_TIMEZONES = [
  // EU Member States
  'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Athens', 'Europe/Berlin',
  'Europe/Bratislava', 'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest',
  'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Helsinki', 'Europe/Lisbon',
  'Europe/Ljubljana', 'Europe/Luxembourg', 'Europe/Madrid', 'Europe/Malta',
  'Europe/Monaco', 'Europe/Oslo', 'Europe/Paris', 'Europe/Prague',
  'Europe/Riga', 'Europe/Rome', 'Europe/San_Marino', 'Europe/Sarajevo',
  'Europe/Skopje', 'Europe/Sofia', 'Europe/Stockholm', 'Europe/Tallinn',
  'Europe/Tirane', 'Europe/Vaduz', 'Europe/Vatican', 'Europe/Vienna',
  'Europe/Vilnius', 'Europe/Warsaw', 'Europe/Zagreb', 'Europe/Zurich',
  // UK
  'Europe/London', 'Europe/Belfast', 'Europe/Guernsey', 'Europe/Isle_of_Man', 'Europe/Jersey',
  // EEA (Norway, Iceland, Liechtenstein)
  'Atlantic/Reykjavik', 'Europe/Oslo',
  // Other European territories with GDPR-like laws
  'Europe/Gibraltar', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores',
  // Swiss data protection (similar requirements)
  'Europe/Zurich'
];

// Also check for Brazil (LGPD), Canada (PIPEDA) which have similar requirements
const OTHER_CONSENT_TIMEZONES = [
  'America/Sao_Paulo', 'America/Fortaleza', 'America/Recife', 'America/Bahia',
  'America/Belem', 'America/Manaus', 'America/Cuiaba', 'America/Campo_Grande'
];

export default {
  name: 'CookieNotice',
  data() {
    return {
      showNotice: false
    };
  },
  mounted() {
    // Check if user has already made a choice
    const cookieChoice = localStorage.getItem('cookieConsent');
    if (cookieChoice) {
      // User already made a choice, don't show
      return;
    }

    // Check if user is in a region requiring cookie consent
    if (this.requiresCookieConsent()) {
      // Show notice after a short delay for better UX
      setTimeout(() => {
        this.showNotice = true;
      }, 1000);
    } else {
      // For non-GDPR regions (like US), auto-accept and don't show banner
      localStorage.setItem('cookieConsent', 'auto-accepted');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      localStorage.setItem('cookieConsentRegion', 'non-gdpr');
      this.$emit('cookies-accepted');
    }
  },
  methods: {
    requiresCookieConsent() {
      try {
        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Check if timezone is in GDPR regions
        if (GDPR_TIMEZONES.includes(timezone)) {
          return true;
        }
        
        // Check for other consent-requiring regions
        if (OTHER_CONSENT_TIMEZONES.includes(timezone)) {
          return true;
        }
        
        // Also check if timezone starts with 'Europe/' as a fallback
        if (timezone && timezone.startsWith('Europe/')) {
          return true;
        }
        
        return false;
      } catch (e) {
        // If we can't determine timezone, show the notice to be safe
        console.warn('Could not determine timezone for cookie consent:', e);
        return true;
      }
    },

    acceptCookies() {
      localStorage.setItem('cookieConsent', 'accepted');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      this.hideNotice();
      
      // Emit event for analytics initialization
      this.$emit('cookies-accepted');
    },
    
    declineCookies() {
      localStorage.setItem('cookieConsent', 'declined');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      this.hideNotice();
      
      // Emit event for analytics cleanup
      this.$emit('cookies-declined');
    },
    
    hideNotice() {
      this.showNotice = false;
    }
  }
};
</script>

<style scoped>
/* Smooth slide-up animation */
.fixed {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
